import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Hreflang market definitions for international SEO
const HREFLANG_MARKETS = [
  { lang: 'en-US', region: 'US' },
  { lang: 'en-GB', region: 'GB' },
  { lang: 'en-AU', region: 'AU' },
  { lang: 'en-CA', region: 'CA' },
  { lang: 'en-NZ', region: 'NZ' },
  { lang: 'en-IE', region: 'IE' },
] as const;

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  structuredData?: object;
  noIndex?: boolean;
  geoRegion?: string;
  /** Set to false to skip hreflang tags (e.g. on noIndex pages) */
  hreflang?: boolean;
  /** areaServed for Service schema (e.g. "Florida", "Mediterranean") */
  areaServed?: string | string[];
}

export default function SEO({ 
  title, 
  description, 
  keywords, 
  canonicalUrl,
  ogType = 'website',
  ogImage,
  structuredData,
  noIndex = false,
  geoRegion,
  hreflang = true,
  areaServed,
}: SEOProps) {
  const [override, setOverride] = useState<{ title?: string | null; meta_description?: string | null; og_image?: string | null; canonical_url?: string | null; extra_jsonld?: any } | null>(null);

  // Fetch admin-approved override for this path (Phase 5: Assisted Mode)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('page_metadata_overrides')
          .select('title, meta_description, og_image, canonical_url, extra_jsonld')
          .eq('url_path', path)
          .maybeSingle();
        if (!cancelled && data) setOverride(data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply overrides over caller-supplied props
  const effectiveTitle = override?.title || title;
  const effectiveDescription = override?.meta_description || description;
  const effectiveOgImage = override?.og_image || ogImage;
  const effectiveCanonical = override?.canonical_url || canonicalUrl;
  const overrideJsonLd = override?.extra_jsonld;

  useEffect(() => {
    // Update title
    if (effectiveTitle) {
      document.title = effectiveTitle;
    }

    // Update meta description
    if (effectiveDescription) {
      updateMetaTag('description', effectiveDescription);
    }

    // Update keywords
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update canonical URL
    if (effectiveCanonical) {
      updateCanonicalUrl(effectiveCanonical);
    }

    // Update Open Graph tags
    if (effectiveTitle) {
      updateMetaProperty('og:title', effectiveTitle);
    }
    if (effectiveDescription) {
      updateMetaProperty('og:description', effectiveDescription);
    }
    if (ogType) {
      updateMetaProperty('og:type', ogType);
    }
    if (effectiveCanonical) {
      updateMetaProperty('og:url', effectiveCanonical);
    }
    if (effectiveOgImage) {
      updateMetaProperty('og:image', effectiveOgImage);
    }

    // Update Twitter tags
    if (effectiveTitle) {
      updateMetaProperty('twitter:title', effectiveTitle);
    }
    if (effectiveDescription) {
      updateMetaProperty('twitter:description', effectiveDescription);
    }
    if (effectiveOgImage) {
      updateMetaProperty('twitter:image', effectiveOgImage);
    }

    // Ensure robots meta tag
    updateRobotsTag(noIndex);

    // Update geo.region meta tag
    if (geoRegion) {
      updateMetaTag('geo.region', geoRegion);
    }

    // Hreflang alternate links for international SEO
    if (hreflang && effectiveCanonical && !noIndex) {
      updateHreflangTags(effectiveCanonical);
    }

    // areaServed Service schema
    if (areaServed && effectiveTitle) {
      addAreaServedSchema(effectiveTitle, effectiveDescription || '', effectiveCanonical || '', areaServed);
    }

    // Add structured data
    if (structuredData) {
      addStructuredData(structuredData);
    }

    // Inject extra JSON-LD from override
    if (overrideJsonLd) {
      addOverrideJsonLd(overrideJsonLd);
    }
  }, [effectiveTitle, effectiveDescription, keywords, effectiveCanonical, ogType, effectiveOgImage, structuredData, noIndex, geoRegion, hreflang, areaServed, overrideJsonLd]);

  const updateMetaTag = (name: string, content: string) => {
    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  const updateMetaProperty = (property: string, content: string) => {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  const updateRobotsTag = (shouldNoIndex: boolean) => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', shouldNoIndex ? 'noindex, nofollow' : 'index, follow');
  };

  const updateCanonicalUrl = (url: string) => {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  };

  const updateHreflangTags = (canonical: string) => {
    // Remove existing hreflang tags
    document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());

    // Extract path from canonical URL
    try {
      const url = new URL(canonical);
      const path = url.pathname + url.search;

      HREFLANG_MARKETS.forEach(({ lang }) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        link.setAttribute('data-hreflang', 'true');
        link.setAttribute('href', `https://yachtvalues.com${path}`);
        document.head.appendChild(link);
      });

      // x-default (fallback for unmatched regions)
      const xDefault = document.createElement('link');
      xDefault.setAttribute('rel', 'alternate');
      xDefault.setAttribute('hreflang', 'x-default');
      xDefault.setAttribute('data-hreflang', 'true');
      xDefault.setAttribute('href', `https://yachtvalues.com${path}`);
      document.head.appendChild(xDefault);
    } catch {
      // Invalid URL, skip
    }
  };

  const addAreaServedSchema = (name: string, desc: string, url: string, areas: string | string[]) => {
    const existingScript = document.querySelector('script[data-area-served="true"]');
    if (existingScript) existingScript.remove();

    const areaList = Array.isArray(areas) ? areas : [areas];
    const schema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": name,
      "description": desc,
      ...(url && { "url": url }),
      "provider": {
        "@type": "Organization",
        "name": "YachtValues",
        "url": "https://yachtvalues.com",
      },
      "areaServed": areaList.map(area => ({
        "@type": "Place",
        "name": area,
      })),
      "serviceType": "Yacht Valuation & Market Intelligence",
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-area-served', 'true');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  };


  const addStructuredData = (data: object) => {
    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"][data-dynamic="true"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-dynamic', 'true');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  };

  const addOverrideJsonLd = (data: object) => {
    const existing = document.querySelector('script[type="application/ld+json"][data-override="true"]');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-override', 'true');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  };

  return null; // This component doesn't render anything
}

// SEO constants for buyer-focused keywords - 2026 yacht-first
export const BUYER_KEYWORDS = [
  // Yacht buyer keywords (primary)
  'fair price for a yacht',
  'yacht buying guide',
  'what should I pay for a yacht',
  'what should I pay for a motor yacht',
  'luxury yacht fair market value',
  'yacht purchase price guide',
  'yacht price estimator',
  'yacht price checker',
  'first time yacht buyer',
  'yacht buyer tips',
  'buying a yacht checklist',
  'how to negotiate yacht price',
  'avoid overpaying for a yacht',
  'good deal on a yacht',
  // Dual-keyword boat terms (SEO preservation)
  'fair boat price',
  'fair price for a boat',
  'fair market value boat',
  'what should I pay for a boat',
  'how much should I pay for a boat',
  'boat buying price guide',
  'boat purchase price estimator',
  'boat price negotiation',
  'boat buying guide',
  'used boat buying guide',
  'boat price checker',
  'boat price estimator'
].join(', ');

// SEO constants for yacht/boat valuation keywords - 2026 yacht-first with dual-keyword strategy
export const BOAT_VALUATION_KEYWORDS = [
  // Yacht-first core keywords
  'yacht values',
  'yacht value',
  'yacht valuation',
  'yacht appraisal',
  'yacht market value',
  'yacht pricing guide',
  'yacht value calculator',
  'yacht value estimator',
  'yacht trade-in value',
  'yacht market analysis',
  'yacht investment value',
  'yacht depreciation',
  'luxury yacht values',
  'motor yacht values',
  'sailboat values',
  'catamaran values',
  'marine valuation',
  'marine appraisal',
  // Yacht question keywords
  'what is my yacht worth',
  'how much is my yacht worth',
  'value my yacht',
  'sell my yacht',
  'yacht resale value',
  // Dual-keyword boat terms (SEO traffic preservation)
  'boat values',
  'boat value',
  'boat valuation',
  'boat appraisal',
  'boat worth',
  'boat value estimator',
  'boat value calculator',
  'used boat values',
  'boat value lookup',
  'boat value by vin',
  'boat value by hin',
  'boat trade-in value',
  'what is my boat worth',
  'how much is my boat worth',
  // Brand comparisons (high traffic)
  'nada boat values',
  'kelley blue book boats',
  'kbb boat values',
  'jd power boat values',
  // Regional
  'yacht values florida',
  'yacht values california',
  'yacht values texas',
  'boat values florida',
  'boat values california',
  // 2026 keywords
  'confidential yacht valuation',
  'professional yacht appraisal',
  'current yacht values',
  'yacht values guide 2026',
  'accurate yacht values'
].join(', ');

export const BOAT_VALUATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "YachtValues™ - Instant Yacht Valuation",
  "description": "Instant yacht valuation and market intelligence platform for luxury vessels, providing data-driven insights for discerning owners, buyers, and advisors.",
  "url": "https://yachtvalues.com",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Any",
  "browserRequirements": "Modern web browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "Instant yacht valuation service"
  },
  "provider": {
    "@type": "Organization",
    "name": "YachtValues",
    "url": "https://yachtvalues.com",
    "email": "contact@yachtvalues.com"
  },
  "featureList": [
    "AI-powered yacht valuation",
    "Instant market intelligence",
    "Private market analysis and trends",
    "Professional yacht appraisal",
    "Data-driven insights",
    "Comprehensive market reports"
  ]
};